import { useMemo } from "react";
import type { AdvancedFilters, BaseEntity } from "../../../UniversalCRMTable/types";
import type {
  DataState,
  EntityState,
  ExportState,
  FilterState,
} from "../../contexts/types";
import { useCompositionContextParts } from "./useCompositionContextParts";
import type { CompositionContextValueParams } from "./compositionContextValueTypes";

/** Allocated once. `?? {}` inline would hand consumers a new object each rebuild. */
const EMPTY_ADVANCED_FILTERS: AdvancedFilters = {};

/**
 * The VOLATILE half of the page context — the slices that move while the user
 * works: search text and filters, the rows and their loading flag, which item
 * is being edited, export progress.
 *
 * Kept apart from the stable half so that a keystroke invalidates only this,
 * and only for the components that actually render filter or row state.
 */
export interface CompositionVolatileSlices<T extends BaseEntity> {
  dataState: DataState;
  entityState: EntityState<T>;
  filterState: FilterState<T>;
  exportState: ExportState;
}

export function useCompositionVolatileSlices<T extends BaseEntity>(
  params: CompositionContextValueParams<T>
): CompositionVolatileSlices<T> {
  const {
    dataStateWithoutLoading,
    stableSetSerializeFilters,
    stableSetApplyFilters,
    convertToGraphQLCallback,
  } = useCompositionContextParts({
    dataState: params.dataState,
    config: params.config,
    entityName: params.entityName,
  });

  // `isLoading` IS a dependency. It used to be deliberately excluded — the
  // value read `params.dataState.isLoading` while the dep array carried only
  // `dataStateWithoutLoading`, so when only the loading flag flipped every
  // consumer kept reading the PREVIOUS value. `UniversalEntityPage` gates the
  // entire page on exactly that field. Isolating `dataState` into its own memo
  // is what makes an honest dependency affordable: a load flip no longer
  // rebuilds pageConfig/handlers/callbacks along with it.
  const isLoading = params.dataState.isLoading;
  const dataState = useMemo<DataState>(
    () => ({ ...dataStateWithoutLoading, isLoading }),
    [dataStateWithoutLoading, isLoading]
  );

  const entityState = useMemo<EntityState<T>>(
    () => ({
      editingItem: params.entityState.editingItem,
      setEditingItem: params.entityState.setEditingItem,
      deletingItem: params.entityState.deletingItem,
      setDeletingItem: params.entityState.setDeletingItem,
      showComposeModal: params.entityState.showComposeModal,
      setShowComposeModal: params.entityState.setShowComposeModal,
      showExportModal: params.entityState.showExportModal,
      setShowExportModal: params.entityState.setShowExportModal,
      bulkEmailExportData: params.entityState.bulkEmailExportData,
      setBulkEmailExportData: params.entityState.setBulkEmailExportData,
    }),
    [
      params.entityState.editingItem,
      params.entityState.setEditingItem,
      params.entityState.deletingItem,
      params.entityState.setDeletingItem,
      params.entityState.showComposeModal,
      params.entityState.setShowComposeModal,
      params.entityState.showExportModal,
      params.entityState.setShowExportModal,
      params.entityState.bulkEmailExportData,
      params.entityState.setBulkEmailExportData,
    ]
  );

  const exportState = useMemo<ExportState>(
    () => ({
      isExporting: params.entityState.isExporting,
      setIsExporting: params.entityState.setIsExporting,
      exportProgress: params.entityState.exportProgress,
      setExportProgress: params.entityState.setExportProgress,
      showExportModal: params.entityState.showExportModal,
      setShowExportModal: params.entityState.setShowExportModal,
    }),
    [
      params.entityState.isExporting,
      params.entityState.setIsExporting,
      params.entityState.exportProgress,
      params.entityState.setExportProgress,
      params.entityState.showExportModal,
      params.entityState.setShowExportModal,
    ]
  );

  const advancedFilters = params.normalizedCurrentFilters?.advancedFilters ?? EMPTY_ADVANCED_FILTERS;
  const filterState = useMemo<FilterState<T>>(
    () => ({
      searchTerm: params.filterState.searchTerm,
      setSearchTerm: params.filterState.setSearchTerm,
      debouncedSearchTerm: params.filterState.debouncedSearchTerm,
      tableFilters: params.filterState.tableFilters,
      setTableFilters: params.filterState.setTableFilters,
      currentFilters: params.normalizedCurrentFilters,
      setCurrentFilters: params.filterState.setCurrentFilters,
      advancedFilters,
      setAdvancedFilters: params.filterState.setAdvancedFilters,
      filterOperator: params.filterState.filterOperator,
      setFilterOperator: params.filterState.setFilterOperator,
      activeViewId: params.navigation.activeViewId,
      setActiveViewId: params.filterState.setActiveViewId,
      applySavedView: params.filterState.applySavedView,
      serializeFilters: params.filterState.serializeFilters,
      setSerializeFilters: stableSetSerializeFilters,
      applyFilters: params.filterState.applyFilters,
      setApplyFilters: stableSetApplyFilters,
      convertToGraphQL: convertToGraphQLCallback,
    }),
    [
      params.filterState.searchTerm,
      params.filterState.setSearchTerm,
      params.filterState.debouncedSearchTerm,
      params.filterState.tableFilters,
      params.filterState.setTableFilters,
      params.normalizedCurrentFilters,
      params.filterState.setCurrentFilters,
      advancedFilters,
      params.filterState.setAdvancedFilters,
      params.filterState.filterOperator,
      params.filterState.setFilterOperator,
      params.navigation.activeViewId,
      params.filterState.setActiveViewId,
      params.filterState.applySavedView,
      params.filterState.serializeFilters,
      stableSetSerializeFilters,
      params.filterState.applyFilters,
      stableSetApplyFilters,
      convertToGraphQLCallback,
    ]
  );

  return {
    dataState,
    entityState,
    filterState,
    exportState,
  };
}
